import type {
    BaseQueryFn,
    FetchArgs,
} from '@reduxjs/toolkit/query'
import axios, { AxiosError, ResponseType } from 'axios'
import TokenService from 'src/utils/tokenService'
import { refreshAccessToken } from 'src/utils/api'
// TODO: Migrate all API calls to RTK Query 
export interface ExoApiError {
    /** HTTP status code */
    status: number;
    /** Problem details containing error information */
    data: ProblemDetails;
}
export interface ProblemDetails {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;
    [key: string]: any;
}

export const axiosBaseQueryWithReauth = ({ baseUrl } = { baseUrl: '' }): BaseQueryFn<
    FetchArgs & { useAuth?: boolean, headers?: Record<string, string>, responseType?: ResponseType },
    unknown,
    ExoApiError
> => async ({ url, method, body, params, headers = {}, useAuth = true, responseType }, api, extraOptions) => {
    if (useAuth && !headers.Authorization) {
        const accessToken: string = TokenService.getLocalAccessToken();
        headers.Authorization = `Bearer ${accessToken}`;
    }
    headers['x-api-key'] = process.env.REACT_APP_X_API_KEY;

    try {
        const response = await axios.request({
            url: baseUrl + url,
            method,
            headers: headers as Record<string, string>,
            data: body,
            params,
            responseType,
        })
        return { data: response.data, meta: response };
    } catch (error) {
        if (error instanceof AxiosError) {
            if (error.response){
                if (error?.response?.status === 401 && useAuth) {
                    const newToken = await refreshAccessToken();
                    if (!newToken) {
                        return {
                            error: {
                                status: 401,
                                data: {
                                    title: 'Unauthorized',
                                    status: 401,
                                    detail: 'Authentication failed or token expired',
                                    instance: url
                                }
                            },
                            data: undefined
                        };
                    }
                    headers['Authorization'] = `Bearer ${newToken}`;
                    return await axiosBaseQueryWithReauth({ baseUrl })({ url, method, body, params, headers, useAuth: true }, api, extraOptions);
                }
    
                const problemDetails: ProblemDetails = {
                    ...error.response.data,
                    type: error.response.data?.type,
                    title: error.response.data?.title ?? error.message ?? 'Server Error',
                    status: error.response.status,
                    detail: error.response.data?.detail ?? error.response.statusText ?? 'An error occurred while processing your request',
                };
                return {
                    error: {
                        status: error.response.status,
                        data: problemDetails
                    },
                    data: undefined
                };
            }
        }
        console.error(error)
        return {
            error: error,
            data: undefined
        }
    }
}
